import { z } from 'zod';
import {
  CashRequestId,
  Currency,
  HouseholdId,
  IsoDatetime,
  LoanId,
  ReceiptId,
  ReimbursementId,
  UserId,
} from './primitives.js';

export const ReceiptCategory = z.enum([
  'groceries',
  'fuel',
  'household',
  'kids',
  'restaurant',
  'healthcare',
  'other',
]);
export type ReceiptCategory = z.infer<typeof ReceiptCategory>;

export const ReceiptOcrStatus = z.enum(['pending', 'extracted', 'failed', 'manual']);

export const Receipt = z.object({
  id: ReceiptId,
  household_id: HouseholdId,
  uploaded_by_user_id: UserId,
  r2_key: z.string(),
  thumbnail_r2_key: z.string().nullable(),
  ocr_status: ReceiptOcrStatus,
  merchant: z.string().nullable(),
  spent_at: IsoDatetime.nullable(),
  amount_minor: z.number().int().nullable(),
  currency: Currency.nullable(),
  category: ReceiptCategory.nullable(),
  approved_at: IsoDatetime.nullable(),
  rejected_at: IsoDatetime.nullable(),
  approved_by_user_id: UserId.nullable(),
  rejection_note: z.string().nullable(),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});
export type Receipt = z.infer<typeof Receipt>;

export const OcrExtraction = z.object({
  is_receipt: z.boolean(),
  merchant: z.string().min(1).max(200).nullable(),
  spent_at: IsoDatetime.nullable(),
  amount_minor: z.number().int().nonnegative().nullable(),
  currency: z.string().length(3).nullable(),
  category: ReceiptCategory.nullable(),
  confidence: z.number().min(0).max(1),
  notes: z.string().max(500).nullable(),
});
export type OcrExtraction = z.infer<typeof OcrExtraction>;

export const RegisterReceiptRequest = z.object({
  r2_key: z.string().min(1),
  // One per logical write, reused across every retry — migration 0050.
  // Usually arrives as the Idempotency-Key header (the route aliases it
  // here, body field winning when both are present); optional either way.
  idempotency_key: z.string().max(64).optional(),
});

export const ReceiptDecisionRequest = z.object({
  note: z.string().max(500).optional(),
});

export const ReimbursementState = z.enum(['submitted', 'approved', 'rejected', 'paid']);
export type ReimbursementState = z.infer<typeof ReimbursementState>;

export const Reimbursement = z.object({
  id: ReimbursementId,
  household_id: HouseholdId,
  staff_user_id: UserId,
  receipt_id: ReceiptId.nullable(),
  amount_minor: z.number().int().positive(),
  currency: Currency,
  state: ReimbursementState,
  note: z.string().nullable(),
  decided_by_user_id: UserId.nullable(),
  decided_at: IsoDatetime.nullable(),
  paid_at: IsoDatetime.nullable(),
  paid_via: z.string().nullable(),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});

export const CreateReimbursementRequest = z.object({
  staff_user_id: UserId,
  amount_minor: z.number().int().positive(),
  currency: Currency,
  receipt_id: ReceiptId.optional(),
  note: z.string().max(500).optional(),
});

export const LoanState = z.enum(['active', 'paid_off', 'written_off']);
export type LoanState = z.infer<typeof LoanState>;

export const Loan = z.object({
  id: LoanId,
  household_id: HouseholdId,
  staff_user_id: UserId,
  principal_minor: z.number().int().positive(),
  currency: Currency,
  reason: z.string().nullable(),
  state: LoanState,
  agreed_at: IsoDatetime,
  closed_at: IsoDatetime.nullable(),
  balance_minor: z.number().int().nonnegative(),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});

export const CreateLoanRequest = z.object({
  staff_user_id: UserId,
  principal_minor: z.number().int().positive(),
  currency: Currency,
  reason: z.string().max(500).optional(),
  agreed_at: IsoDatetime.optional(),
});

export const RecordRepaymentRequest = z.object({
  amount_minor: z.number().int().positive(),
  currency: Currency,
  note: z.string().max(500).optional(),
  paid_at: IsoDatetime.optional(),
});

export const CashRequestState = z.enum([
  'requested',
  'approved',
  'rejected',
  'dispensed',
  'reconciled',
]);
export type CashRequestState = z.infer<typeof CashRequestState>;

export const CashRequest = z.object({
  id: CashRequestId,
  household_id: HouseholdId,
  requested_by_user_id: UserId,
  amount_minor: z.number().int().positive(),
  currency: Currency,
  purpose: z.string().min(1).max(500),
  state: CashRequestState,
  decided_by_user_id: UserId.nullable(),
  decided_at: IsoDatetime.nullable(),
  dispensed_at: IsoDatetime.nullable(),
  reconciled_receipt_id: ReceiptId.nullable(),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});

export const CreateCashRequestRequest = z.object({
  amount_minor: z.number().int().positive(),
  currency: Currency,
  purpose: z.string().min(1).max(500),
});

/**
 * One currency's share of what a household owes. Never a converted mixture —
 * there is no FX in the API, so every figure here is denominated in the
 * `currency` beside it and totals are only ever within a row.
 */
export const OutstandingByCurrency = z.object({
  currency: Currency,
  reimbursements_minor: z.number().int(),
  loans_minor: z.number().int(),
  total_minor: z.number().int(),
});

export const MoneySnapshot = z.object({
  // The primary-currency figure, unchanged in meaning since it shipped: the
  // AED part of an AED household's debt, not necessarily all of its debt.
  outstanding_owed_minor: z.number().int().nonnegative(),
  outstanding_owed_currency: Currency,
  pending_receipts: z.number().int().nonnegative(),
  active_loans: z.number().int().nonnegative(),
  projected_next_month_cost_minor: z.number().int(),
  // What the headline pair above cannot express. `outstanding_by_currency`
  // always includes the primary currency (at zero if there is nothing owed in
  // it), and `excluded_currencies` names the codes the headline omits — empty
  // for a single-currency household, which is what makes it a usable signal.
  outstanding_by_currency: z.array(OutstandingByCurrency),
  excluded_currencies: z.array(Currency),
});
